import { PhoenixTrpcClient, RouterOutput } from 'apps/phoenix/src/app/utils';
import { BehaviorSubject, Subject, defer, finalize, takeUntil } from 'rxjs';

import { Injectable, Renderer2, computed, inject, signal } from '@angular/core';

import { TRPCClientError } from '@trpc/client';

import { ExportHelpers } from '../export-helper/export-helper.service';
import { PromotionalDialogService } from '../promotional-dialog/promotional-dialog.service';
import { UiHelpersService } from '../ui-helpers/ui-helpers.service';

export type ExportStatus = 'PENDING' | 'COMPLETED' | 'FAILED';
export type ExportComponentId = `download-management-${string}`;
export interface DownloadStatus {
  componentId?: ExportComponentId;
  id: string;
  path?: string;
  status?: ExportStatus;
  file?: {
    filename: string;
    url: string;
  };
  progress?: number;
  error?: string;
  createdAt?: string;
}

export interface ExportStatusStorage {
  id: string;
  componentId?: string;
}

interface DownloadData {
  subject: BehaviorSubject<DownloadStatus>;
  intervalId: ReturnType<typeof setInterval> | null;
  destroy$: Subject<void>;
  /** True while a progress request for this download is awaiting a response. */
  inFlight: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class DownloadService {
  private readonly downloadsSignal = signal<Map<string, DownloadData>>(
    new Map()
  );
  private readonly downloadsVersion = signal(0);
  private readonly trpcService = inject(PhoenixTrpcClient);
  private readonly PromotionalService = inject(PromotionalDialogService);
  private readonly uiHelpers = inject(UiHelpersService);

  private readonly intervalTimer = 5000;
  private readonly localStorageKey = 'phoenix-pending-downloads';
  private readonly baseDownloadPath = 'export.progress';

  constructor() {
    this.loadPendingDownloadsFromStorage();
  }

  private loadPendingDownloadsFromStorage(): void {
    try {
      const savedDownloads = localStorage.getItem(this.localStorageKey);
      if (savedDownloads) {
        const pendingDownloads = JSON.parse(savedDownloads);
        if (!Array.isArray(pendingDownloads)) {
          return;
        }

        for (const item of pendingDownloads) {
          // Re-register each download with the minimal info we have
          this.registerDownload({
            id: item.id,
            componentId: (item.componentId as ExportComponentId) ?? undefined,
            path: this.baseDownloadPath,
            status: 'PENDING',
          });
        }
      }
    } catch (error) {
      console.error('Failed to load pending downloads from storage:', error);
    }
  }

  registerDownload(data: DownloadStatus): void {
    if (!this.downloadsSignal().has(data.id)) {
      const initialStatus: DownloadStatus = {
        componentId: (data.componentId as ExportComponentId) ?? undefined,
        status: 'PENDING',
        progress: 0,
        ...data,
      };

      const downloadData: DownloadData = {
        subject: new BehaviorSubject<DownloadStatus>(initialStatus),
        intervalId:
          initialStatus.status === 'PENDING'
            ? this.startPeriodicChecks(data.id)
            : null,
        destroy$: new Subject<void>(),
        inFlight: false,
      };

      this.downloadsSignal.update(map => {
        const newMap = new Map(map);
        newMap.set(data.id, downloadData);
        return newMap;
      });
      this.handleDownload(initialStatus);

      if (initialStatus.status === 'PENDING') {
        this.saveToLocalStorage(initialStatus);
        // Check once straight away. Waiting for the first interval tick leaves
        // every download sitting at 0% for a full interval, even one that the
        // backend has already finished.
        void this.checkDownloadStatus(data.id);
      }
    }
  }

  removeDownload(id: string): void {
    if (!id) return;
    const downloadData = this.downloadsSignal().get(id);
    if (downloadData) {
      downloadData.destroy$.next();
      downloadData.destroy$.complete();
      this.stopPeriodicChecks(id);
      downloadData.subject.complete();
      this.downloadsSignal.update(map => {
        const newMap = new Map(map);
        newMap.delete(id);
        return newMap;
      });
      this.PromotionalService.closeDialog(id);

      this.removeFromLocalStorage(id);
    }
  }

  getAllDownloadsByComponentId(
    componentId: ExportComponentId
  ): DownloadStatus[] {
    return Array.from(this.downloadsSignal().values())
      .filter(data => data.subject.value.componentId === componentId)
      .map(data => data.subject.value);
  }

  isAnyDownloadInProgress(): boolean {
    for (let downloadData of this.downloadsSignal().values()) {
      const status = downloadData.subject.value.status;
      if (status === 'PENDING') {
        return true;
      }
    }
    return false;
  }

  downloadsListNumber(): number {
    return this.downloadsSignal().size;
  }

  downloadsList(): DownloadStatus[] {
    return Array.from(this.downloadsSignal().values()).map(
      downloadData => downloadData.subject.value
    );
  }

  downloadElement(id: string, renderer: Renderer2): void {
    const downloadData = this.downloadsSignal().get(id);
    const currentElement = downloadData?.subject.value;
    if (currentElement?.file?.url) {
      ExportHelpers.downloadLink(renderer, currentElement.file.url);
      this.removeDownload(id);
    } else if (downloadData && currentElement) {
      this.updateDownloadStatus(id, {
        ...currentElement,
        status: 'FAILED',
        error: currentElement.error ?? 'Download URL is not available.',
      });
    } else {
      this.uiHelpers.errorSnackbar(
        `Download not found or invalid state for id: ${id}`
      );
    }
  }

  private createDialogDescription(downloadStatus: DownloadStatus) {
    if (downloadStatus.status === 'COMPLETED') {
      return `Your download is ready. Click here to download.`;
    }
    return (
      downloadStatus.error ??
      'Your download has failed, please try again later.'
    );
  }

  private handleDownload(data: DownloadStatus) {
    if (data.status === 'COMPLETED' || data.status === 'FAILED') {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      this.PromotionalService.addDialog({
        title: 'Download management',
        description: this.createDialogDescription(data),
        storageKey: data.id,
        isVisible: true,
        severity: data.status === 'COMPLETED' ? 'success' : 'error',
        expirationDate: tomorrow,
        isTemporary: true,
        hasDownloadButton: data.status === 'COMPLETED',
        downloadManagementId: data.id,
      });
    }
  }

  private updateDownloadStatus(id: string, status: DownloadStatus): void {
    const downloadData = this.downloadsSignal().get(id);
    if (downloadData) {
      downloadData.subject.next(status);
      this.downloadsVersion.update(v => v + 1);

      if (status.status === 'COMPLETED' || status.status === 'FAILED') {
        this.removeFromLocalStorage(id);
        this.stopPeriodicChecks(id);
        this.handleDownload(status);
      }
    }
  }

  private async checkDownloadStatus(id: string): Promise<void> {
    const downloadData = this.downloadsSignal().get(id);

    if (!downloadData) {
      console.error(`No download registered for id: ${id}`);
      return;
    }

    // Skip this tick while the previous request is still open, so a slow or
    // unanswered backend cannot accumulate one request per interval.
    if (downloadData.inFlight) {
      return;
    }

    const download = downloadData.subject.value;

    if (!download) {
      console.error(`No download registered for id: ${id}`);
      return;
    }

    downloadData.inFlight = true;

    defer(
      () =>
        this.trpcRequest(download.path ?? this.baseDownloadPath).query({
          taskId: download.id,
        }) as Promise<RouterOutput['export']['progress']>
    )
      .pipe(
        takeUntil(downloadData.destroy$),
        finalize(() => {
          downloadData.inFlight = false;
        })
      )
      .subscribe({
        next: (result: RouterOutput['export']['progress']) => {
          const status: DownloadStatus = {
            ...download,
            id: result.id,
            status: result.status,
            file: result.file,
            progress: result.progress || 0,
            error: result.error as string,
          };

          this.updateDownloadStatus(id, status);
        },
        error: () => {
          this.updateDownloadStatus(id, {
            ...download,
            status: 'FAILED',
          });
        },
      });
  }

  async cancelDownload(id: string): Promise<void> {
    const download = this.downloadsSignal().get(id)?.subject.value;

    if (!download) {
      this.uiHelpers.errorSnackbar('Download not found');
      return;
    }

    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    try {
      await this.trpcRequest('export.cancel').mutate({
        taskId: download.id,
      });
      this.removeDownload(id);

      this.PromotionalService.addDialog({
        title: 'Download management',
        description: 'Download cancelled successfully',
        storageKey: `cancelled-${id}`,
        isVisible: true,
        severity: 'info',
        expirationDate: tomorrow,
        isTemporary: true,
        hasDownloadButton: false,
      });
    } catch (error: unknown) {
      if (error instanceof TRPCClientError) {
        const code = error.data?.code;
        const message = error.message;

        if (code === 'PRECONDITION_FAILED') {
          this.PromotionalService.addDialog({
            title: 'Download management',
            description: message,
            storageKey: `cancel-error-${id}`,
            isVisible: true,
            severity: 'error',
            expirationDate: tomorrow,
            isTemporary: true,
            hasDownloadButton: false,
          });
          return;
        }
      }

      this.uiHelpers.handleErrorMessage(error, 'Failed to cancel download');
    }
  }

  private trpcRequest(path: string) {
    const keys = path.split('.');

    const service = keys.reduce((acc: any, key: string) => {
      if (acc?.[key]) {
        return acc[key];
      } else {
        throw new Error(`Path '${path}' is invalid at '${key}'`);
      }
    }, this.trpcService);
    return service;
  }

  private startPeriodicChecks(
    id: string,
    interval: number = this.intervalTimer
  ): ReturnType<typeof setInterval> {
    return setInterval(async () => {
      await this.checkDownloadStatus(id);
    }, interval);
  }

  private stopPeriodicChecks(id: string): void {
    const downloadData = this.downloadsSignal().get(id);
    if (downloadData?.intervalId) {
      clearInterval(downloadData.intervalId);
    }
  }

  private saveToLocalStorage(data: DownloadStatus): void {
    try {
      // Get existing items from localStorage or initialize an empty array
      const existingItems = localStorage.getItem(this.localStorageKey);
      const pendingDownloads = existingItems ? JSON.parse(existingItems) : [];

      // Check if this item already exists in the array
      const existingIndex = pendingDownloads.findIndex(
        (item: ExportStatusStorage) => item.id === data.id
      );

      // Create a simple object with just the needed properties
      const simpleItem = {
        id: data.id,
        ...(data.componentId && { componentId: data.componentId }),
      };

      // Update or add the item
      if (existingIndex !== -1) {
        pendingDownloads[existingIndex] = simpleItem;
      } else {
        pendingDownloads.push(simpleItem);
      }

      // Save back to localStorage
      localStorage.setItem(
        this.localStorageKey,
        JSON.stringify(pendingDownloads)
      );
    } catch (error) {
      console.error('Error saving download to local storage:', error);
    }
  }

  private removeFromLocalStorage(id: string): void {
    try {
      // Get existing items from localStorage
      const existingItems = localStorage.getItem(this.localStorageKey);
      if (!existingItems) return;

      const pendingDownloads = JSON.parse(existingItems);

      // Filter out the item with the matching id
      const filteredDownloads = pendingDownloads.filter(
        (item: ExportStatusStorage) => item.id !== id
      );

      // Save back to localStorage or remove if empty
      if (filteredDownloads.length > 0) {
        localStorage.setItem(
          this.localStorageKey,
          JSON.stringify(filteredDownloads)
        );
      } else {
        localStorage.removeItem(this.localStorageKey);
      }
    } catch (error) {
      console.error('Error removing download from local storage:', error);
    }
  }

  /**
   * Creates a computed signal that checks if there is an active download in progress.
   * @param componentId - The component ID to check
   * @returns A computed signal that returns true if download is in progress
   */
  hasDownloadInProgress(componentId: ExportComponentId) {
    return computed(() => {
      this.downloadsVersion();
      return Array.from(this.downloadsSignal().values()).some(downloadData => {
        const download = downloadData.subject.value;
        return (
          download.componentId === componentId && download.status === 'PENDING'
        );
      });
    });
  }
}
